import React, { useCallback, useContext, useEffect, useState } from "react";
import Styles from "../styles/Account.module.css";
import { supabase } from "../lib/supabaseClient";
import { UserContext } from "./context/user/UserContext";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCrown,
  faGift,
  faSignOutAlt,
  faTrashAlt,
  faUserCircle,
} from "@fortawesome/free-solid-svg-icons";
import { EULA_URL } from "../utils/legal";
import { showSuccess, showError, showWarning } from "../utils/toastUtils";
import Loader from "./Loader";

const isValidReferralCode = (code) => /^[A-Z0-9]{4}$/.test(code);

const Account = () => {
  const { userData, logout } = useContext(UserContext);
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [codeInput, setCodeInput] = useState("");
  const [codeAvailable, setCodeAvailable] = useState(null);
  const [codeChecking, setCodeChecking] = useState(false);
  const [savingCode, setSavingCode] = useState(false);

  const [applyInput, setApplyInput] = useState("");
  const [applyingCode, setApplyingCode] = useState(false);

  const userId = userData?.user?.id;

  const loadAccount = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data: profileData, error } = await supabase
        .from("profiles")
        .select(
          "id, username, email, role, is_premium, subscription_status, subscription_plan, subscription_expires_at, referral_code, referral_count, referred_by, created_at",
        )
        .eq("id", userId)
        .single();

      if (error) throw error;
      setProfile(profileData);

      const { data: stats } = await supabase.rpc("get_referral_stats", {
        p_user_id: userId,
      });

      setReferrals(stats?.referrals || []);
      setReferrer(stats?.referred_by || null);
    } catch (error) {
      showError(error.message || "Could not load your account.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const handleCodeChange = async (value) => {
    const cleaned = value
      .replace(/[^A-Za-z0-9]/g, "")
      .slice(0, 4)
      .toUpperCase();
    setCodeInput(cleaned);
    setCodeAvailable(null);

    if (cleaned.length === 4) {
      setCodeChecking(true);
      try {
        const { data } = await supabase
          .from("profiles")
          .select("id")
          .eq("referral_code", cleaned)
          .neq("id", userId)
          .maybeSingle();
        setCodeAvailable(!data);
      } catch (error) {
        setCodeAvailable(null);
      } finally {
        setCodeChecking(false);
      }
    }
  };

  const handleSaveCode = async () => {
    if (!isValidReferralCode(codeInput)) {
      showWarning("Your code must be exactly 4 letters/numbers.");
      return;
    }

    if (codeAvailable === false) {
      showWarning("That code is already taken. Please choose another.");
      return;
    }

    setSavingCode(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .update({ referral_code: codeInput })
        .eq("id", userId)
        .select("referral_code")
        .single();

      if (error) throw error;

      setProfile((prev) => ({ ...prev, referral_code: data.referral_code }));
      setCodeInput("");
      setCodeAvailable(null);
      showSuccess("Your referral code is ready to share!");
    } catch (error) {
      showError(error.message || "Could not save your referral code.");
    } finally {
      setSavingCode(false);
    }
  };

  const handleCopyCode = async () => {
    if (!profile?.referral_code) return;
    try {
      await navigator.clipboard.writeText(profile.referral_code);
      showSuccess("Referral code copied!");
    } catch (error) {
      showWarning(`Your code is ${profile.referral_code}`);
    }
  };

  const handleApplyCode = async () => {
    const cleaned = applyInput
      .replace(/[^A-Za-z0-9]/g, "")
      .slice(0, 4)
      .toUpperCase();

    if (!isValidReferralCode(cleaned)) {
      showWarning("Referral codes are 4 letters/numbers.");
      return;
    }

    setApplyingCode(true);
    try {
      const { data, error } = await supabase.rpc("process_referral", {
        new_user_id: userId,
        referrer_identifier: cleaned,
      });

      if (error) throw error;
      if (data && !data.success) throw new Error(data.error || "Invalid code");

      showSuccess(`Referral applied — welcome from @${data.referrer_username}!`);
      setApplyInput("");
      await loadAccount();
    } catch (error) {
      showError(error.message || "Could not apply that referral code.");
    } finally {
      setApplyingCode(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading && !profile) {
    return (
      <div className={Styles.container}>
        <Loader text="Loading your account" />
      </div>
    );
  }

  const isPremium =
    profile?.is_premium &&
    (!profile?.subscription_expires_at ||
      new Date(profile.subscription_expires_at) > new Date());

  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })
    : "—";

  return (
    <div className={Styles.container}>
      <div className={Styles.header}>
        <div>
          <h1 className={Styles.title}>My Account</h1>
          <p className={Styles.subtitle}>
            Manage your profile, referrals and subscription.
          </p>
        </div>
        <button className={Styles.dashboardButton} onClick={() => navigate("/")}>
          Back to Home
        </button>
      </div>

      <div className={Styles.grid}>
        {/* Profile */}
        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <FontAwesomeIcon icon={faUserCircle} className={Styles.cardIcon} />
            <h2 className={Styles.cardTitle}>Profile</h2>
          </div>
          <div className={Styles.row}>
            <span className={Styles.rowLabel}>Username</span>
            <span className={Styles.rowValue}>@{profile?.username}</span>
          </div>
          <div className={Styles.row}>
            <span className={Styles.rowLabel}>Email</span>
            <span className={Styles.rowValue}>{profile?.email}</span>
          </div>
          <div className={Styles.row}>
            <span className={Styles.rowLabel}>Member since</span>
            <span className={Styles.rowValue}>{memberSince}</span>
          </div>
          <div className={Styles.row}>
            <span className={Styles.rowLabel}>Status</span>
            <span
              className={`${Styles.badge} ${
                isPremium ? Styles.badgePremium : Styles.badgeFree
              }`}
            >
              {isPremium ? "Pro" : "Free"}
            </span>
          </div>
        </div>

        {/* Subscription */}
        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <FontAwesomeIcon icon={faCrown} className={Styles.cardIcon} />
            <h2 className={Styles.cardTitle}>Subscription</h2>
          </div>
          {isPremium ? (
            <>
              <div className={Styles.row}>
                <span className={Styles.rowLabel}>Plan</span>
                <span className={Styles.rowValue}>
                  {profile?.subscription_plan
                    ? profile.subscription_plan === "monthly"
                      ? "Pro Monthly"
                      : "Pro Annual"
                    : "Pro"}
                </span>
              </div>
              {profile?.subscription_expires_at && (
                <div className={Styles.row}>
                  <span className={Styles.rowLabel}>Renews / expires</span>
                  <span className={Styles.rowValue}>
                    {new Date(
                      profile.subscription_expires_at,
                    ).toLocaleDateString("en-US")}
                  </span>
                </div>
              )}
              <p className={Styles.muted}>
                Manage or cancel your subscription from your App Store or Google
                Play account settings.
              </p>
            </>
          ) : (
            <>
              <div className={Styles.plans}>
                <div className={Styles.plan}>
                  <span className={Styles.planName}>Pro Monthly</span>
                  <span className={Styles.planPrice}>
                    $20<small>/month</small>
                  </span>
                  <ul className={Styles.planFeatures}>
                    <li>Unlimited location views</li>
                    <li>All truck stops, rest areas &amp; parking</li>
                    <li>Add &amp; report locations</li>
                    <li>Priority support</li>
                  </ul>
                </div>
                <div className={`${Styles.plan} ${Styles.planFeatured}`}>
                  <span className={Styles.planBadge}>Best value</span>
                  <span className={Styles.planName}>Pro Annual</span>
                  <span className={Styles.planPrice}>
                    $199<small>/year</small>
                  </span>
                  <span className={Styles.planSavings}>
                    Save $41 vs monthly
                  </span>
                  <ul className={Styles.planFeatures}>
                    <li>Everything in Pro Monthly</li>
                    <li>Unlimited views for a full year</li>
                    <li>Add &amp; report locations</li>
                    <li>Priority support</li>
                  </ul>
                </div>
              </div>
              <p className={Styles.muted}>
                Pro subscriptions are billed through the App Store or Google
                Play. Subscribe from the RigRestR mobile app to upgrade
                instantly.
              </p>
            </>
          )}
        </div>

        {/* Referrals */}
        <div className={`${Styles.card} ${Styles.cardWide}`}>
          <div className={Styles.cardHeader}>
            <FontAwesomeIcon icon={faGift} className={Styles.cardIcon} />
            <h2 className={Styles.cardTitle}>Referral Program</h2>
          </div>

          {profile?.referral_code ? (
            <div className={Styles.codeBlock}>
              <div>
                <span className={Styles.rowLabel}>Your referral code</span>
                <div className={Styles.code}>{profile.referral_code}</div>
              </div>
              <button className={Styles.copyButton} onClick={handleCopyCode}>
                Copy Code
              </button>
            </div>
          ) : (
            <div className={Styles.codeSetup}>
              <p className={Styles.muted}>
                Pick a 4-character code to start inviting drivers. You both get
                rewarded when they join.
              </p>
              <div className={Styles.inputRow}>
                <input
                  className={Styles.input}
                  placeholder="AB12"
                  value={codeInput}
                  onChange={(event) => handleCodeChange(event.target.value)}
                  maxLength={4}
                />
                <button
                  className={Styles.primaryButton}
                  onClick={handleSaveCode}
                  disabled={savingCode || codeInput.length !== 4}
                >
                  {savingCode ? "Saving..." : "Set My Code"}
                </button>
              </div>
              {codeChecking ? (
                <span className={Styles.muted}>Checking availability...</span>
              ) : codeAvailable === true ? (
                <span className={Styles.available}>✓ Available</span>
              ) : codeAvailable === false ? (
                <span className={Styles.unavailable}>✗ Already taken</span>
              ) : null}
            </div>
          )}

          <div className={Styles.row}>
            <span className={Styles.rowLabel}>Drivers referred</span>
            <span className={Styles.rowValue}>
              {profile?.referral_count || referrals.length || 0}
            </span>
          </div>

          {referrer && (
            <div className={Styles.row}>
              <span className={Styles.rowLabel}>You were referred by</span>
              <span className={Styles.rowValue}>@{referrer.username}</span>
            </div>
          )}

          {!referrer && (
            <div className={Styles.applyBlock}>
              <span className={Styles.rowLabel}>Have a referral code?</span>
              <div className={Styles.inputRow}>
                <input
                  className={Styles.input}
                  placeholder="Enter 4-char code"
                  value={applyInput}
                  onChange={(event) => setApplyInput(event.target.value)}
                  maxLength={4}
                />
                <button
                  className={Styles.secondaryButton}
                  onClick={handleApplyCode}
                  disabled={applyingCode || applyInput.length !== 4}
                >
                  {applyingCode ? "Applying..." : "Apply"}
                </button>
              </div>
            </div>
          )}

          {referrals.length > 0 && (
            <div className={Styles.referralList}>
              <span className={Styles.rowLabel}>People you referred</span>
              <ul>
                {referrals.map((referral) => (
                  <li key={referral.id}>
                    <span>@{referral.username}</span>
                    <span className={Styles.muted}>
                      {new Date(referral.referred_at).toLocaleDateString(
                        "en-US",
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Legal & account actions */}
        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <FontAwesomeIcon icon={faArrowRight} className={Styles.cardIcon} />
            <h2 className={Styles.cardTitle}>Legal & Account</h2>
          </div>
          <Link className={Styles.linkRow} to="/terms">
            Terms of Service &amp; Privacy Policy
          </Link>
          <a
            className={Styles.linkRow}
            href={EULA_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            End User License Agreement (EULA)
          </a>
          <Link className={`${Styles.linkRow} ${Styles.danger}`} to="/delete">
            <FontAwesomeIcon icon={faTrashAlt} /> Delete My Account
          </Link>
          <button
            className={`${Styles.linkRow} ${Styles.logout}`}
            onClick={handleLogout}
          >
            <FontAwesomeIcon icon={faSignOutAlt} /> Log Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Account;
